// PlanComparisonStep.tsx — Free vs Premium side-by-side picker
// Replaces the old DemoStep. After picking, the wizard branches into either
// the slim free flow or the full premium wizard.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Stack,
  Chip,
  Collapse,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Paper,
  TextField,
} from '@mui/material';
import { ExpandMore, ExpandLess, CheckCircle } from '@mui/icons-material';
import api from '../../services/api';
import { trackEvent, AnalyticsEvents } from '../../services/analytics';

const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_FAMILY_MONTHLY || null;

interface DemoMeal {
  recipe_id: number;
  name: string;
  description: string;
  image_url?: string;
  meal_type: string;
}

interface DemoDay {
  dayLabel: string;
  meals: {
    breakfast?: DemoMeal;
    lunch?: DemoMeal;
    dinner?: DemoMeal;
  };
}

interface DemoResponse {
  days: DemoDay[];
  filledSlots: number;
}

type UserPath = 'free' | 'premium';

interface Props {
  /** Called when the free path is picked. Premium path is handled internally
   *  (Stripe Checkout redirect or comp-premium promo applied → return to wizard). */
  onSelect: (path: UserPath) => void;
}

interface AppliedPromo {
  source: 'db' | 'stripe';
  code: string;
  description: string;
  stripe_promotion_code_id?: string;
}

const MEAL_TYPES: Array<'breakfast' | 'lunch' | 'dinner'> = ['breakfast', 'lunch', 'dinner'];

const PlanComparisonStep: React.FC<Props> = ({ onSelect }) => {
  // Default the demo to open so users see a colorful preview immediately.
  const [demoOpen, setDemoOpen] = useState(true);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoData, setDemoData] = useState<DemoResponse | null>(null);
  const [selectedDay, setSelectedDay] = useState(0);

  // Promo code state — only surfaced when the user clicks "Have a promo code?"
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Purchase state for Premium tap
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent(AnalyticsEvents.ONBOARDING_STEP_VIEWED, { step_name: 'PlanComparison' });
  }, []);

  // Lazily load demo data when user clicks to expand
  useEffect(() => {
    if (!demoOpen || demoData) return;
    setDemoLoading(true);
    api.get('/meal-plans/demo')
      .then(res => setDemoData(res.data))
      .catch(() => {})
      .finally(() => setDemoLoading(false));
  }, [demoOpen, demoData]);

  const handleFreePick = () => {
    trackEvent(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, {
      step_name: 'PlanComparison',
      selected_path: 'free',
    });
    onSelect('free');
  };

  const validatePromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    setPromoError(null);
    try {
      const { data } = await api.post('/promo-codes/validate', { code });
      setAppliedPromo({
        source: data.source,
        code,
        description: data.description || 'Promo code applied',
        stripe_promotion_code_id: data.stripe_promotion_code_id,
      });
    } catch (err: any) {
      setPromoError(err.response?.data?.message || 'Invalid or expired promo code');
      setAppliedPromo(null);
    } finally {
      setPromoBusy(false);
    }
  };

  /**
   * Premium tap fires Stripe Checkout immediately. On success the user returns
   * to /onboarding?stripe=success and the wizard resumes at step 1.
   * If a creator/chef DB promo is applied, comp-premium is granted directly
   * and we advance without leaving the SPA.
   */
  const handlePremiumPick = async () => {
    setPurchaseError(null);
    setPurchasing(true);
    trackEvent(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, {
      step_name: 'PlanComparison',
      selected_path: 'premium',
      promo_applied: !!appliedPromo,
    });
    try {
      // DB promo (creator/chef code) — grants 30 days comp Premium, no Stripe sub
      if (appliedPromo?.source === 'db') {
        await api.post('/promo-codes/apply-comp-premium', { code: appliedPromo.code });
        onSelect('premium');
        return;
      }
      // Standard path — Stripe Checkout (with optional discount code)
      if (!MONTHLY_PRICE_ID) {
        setPurchaseError('Subscription not configured. Please contact support.');
        return;
      }
      const response = await api.post('/subscriptions/create-checkout-session', {
        priceId: MONTHLY_PRICE_ID,
        successUrl: `${window.location.origin}/onboarding?stripe=success`,
        cancelUrl: `${window.location.origin}/onboarding?stripe=canceled`,
        stripePromotionCodeId: appliedPromo?.stripe_promotion_code_id,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      setPurchaseError(err.response?.data?.message || err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Box maxWidth={900} mx="auto">
      <Typography variant="h4" align="center" sx={{ mb: 1, fontWeight: 600 }}>
        Choose your Snack'd experience
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        You can upgrade or change your plan anytime.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Free card */}
        <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>Free</Typography>
              <Chip size="small" label="No credit card" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Browse our recipe library and build meal plans manually. We adjust serving sizes to fit your household.
            </Typography>

            <Stack spacing={1} sx={{ mb: 3 }}>
              <Feature text="Browse our full recipe library" />
              <Feature text="Add recipes to your plan manually" />
              <Feature text="Auto-scaled serving sizes for your household" />
              <Feature text="Shopping lists generated from your plan" />
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Quick setup (~30 seconds)
            </Typography>
          </CardContent>
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={handleFreePick}
              disabled={purchasing}
            >
              Continue with Free
            </Button>
          </Box>
        </Card>

        {/* Premium card */}
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderColor: 'primary.main',
            borderWidth: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 2,
              py: 0.5,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.5px',
            }}
          >
            RECOMMENDED
          </Box>
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>Premium</Typography>
              <Chip size="small" color="primary" label="7-day free trial" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We collect your family's dietary needs and craft a weekly plan tailored to everyone.
            </Typography>

            <Stack spacing={1} sx={{ mb: 3 }}>
              {/* "Includes everything in Free" callout up top with coral asterisk
                  so users see the carry-over benefit before the premium-only items. */}
              <Feature text="Includes everything in Free" asterisk />
              <Feature text="Personalized weekly meal plans" />
              <Feature text="Tailored to each family member's dietary needs" />
              <Feature text="Handles mixed diets (vegetarian + omnivore, etc.)" />
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              $7.99/mo after the 7-day trial. Cancel anytime.
            </Typography>

            {/* Promo code (collapsed by default) */}
            {!promoOpen && !appliedPromo && (
              <Button
                variant="text"
                size="small"
                onClick={() => setPromoOpen(true)}
                sx={{ mb: 1, p: 0, textTransform: 'none' }}
              >
                Have a promo code?
              </Button>
            )}
            {(promoOpen || appliedPromo) && (
              <Box sx={{ mb: 2 }}>
                {appliedPromo ? (
                  <Alert
                    severity="success"
                    onClose={() => { setAppliedPromo(null); setPromoInput(''); setPromoError(null); }}
                    sx={{ py: 0.5 }}
                  >
                    <Typography variant="caption"><strong>{appliedPromo.code}</strong>: {appliedPromo.description}</Typography>
                  </Alert>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      placeholder="Promo code"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      disabled={promoBusy}
                      fullWidth
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={validatePromo}
                      disabled={promoBusy || !promoInput.trim()}
                    >
                      Apply
                    </Button>
                  </Stack>
                )}
                {promoError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {promoError}
                  </Typography>
                )}
              </Box>
            )}

            {purchaseError && (
              <Alert severity="error" sx={{ mb: 2 }}>{purchaseError}</Alert>
            )}
          </CardContent>
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handlePremiumPick}
              disabled={purchasing}
              startIcon={purchasing ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {purchasing ? 'Starting…' : 'Start 7-day Free Trial'}
            </Button>
          </Box>
        </Card>
      </Box>

      {/* Optional collapsible demo */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="text"
          endIcon={demoOpen ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setDemoOpen(prev => !prev)}
        >
          {demoOpen ? 'Hide example' : 'See an example Premium meal plan'}
        </Button>
        <Collapse in={demoOpen}>
          <Paper variant="outlined" sx={{ mt: 2, p: 3 }}>
            <Typography
              variant="body2"
              sx={{ display: 'block', mb: 2, color: 'secondary.main', fontWeight: 600 }}
            >
              Example only — Premium subscribers get a plan tailored to their family.
            </Typography>
            {demoLoading && <CircularProgress size={24} />}
            {!demoLoading && demoData && demoData.days && demoData.filledSlots > 0 && (
              <>
                <Tabs
                  value={selectedDay}
                  onChange={(_, v) => setSelectedDay(v)}
                  variant="fullWidth"
                  sx={{ mb: 2 }}
                >
                  {demoData.days.map((day, i) => (
                    <Tab key={i} label={day.dayLabel} />
                  ))}
                </Tabs>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                  {MEAL_TYPES.map(mealType => {
                    const meal = demoData.days[selectedDay]?.meals[mealType];
                    return (
                      <Card key={mealType} variant="outlined" sx={{ overflow: 'hidden' }}>
                        {meal?.image_url ? (
                          <CardMedia
                            component="img"
                            height={120}
                            image={meal.image_url}
                            alt={meal.name}
                            sx={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 120,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" color="text.disabled">No image</Typography>
                          </Box>
                        )}
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}
                          >
                            {mealType}
                          </Typography>
                          {meal ? (
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }} noWrap>
                              {meal.name}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>—</Typography>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </>
            )}
            {!demoLoading && (!demoData || demoData.filledSlots === 0) && (
              <Alert severity="info">Example meal plan unavailable right now.</Alert>
            )}
          </Paper>
        </Collapse>
      </Box>
    </Box>
  );
};

// `highlight` is now the default — feedback was that the action-grey checkmarks
// on the Free card read as "you DON'T get these" instead of "you do."
// `asterisk` mode is used for the "Includes everything in Free" callout on the
// Premium card: bold coral '*' instead of a checkmark to draw the eye and signal
// "this is a callback to the other column."
const ACCENT_CORAL = '#F78764'; // brand accent color (see theme.ts)

const Feature: React.FC<{ text: string; asterisk?: boolean }> = ({ text, asterisk }) => (
  <Stack direction="row" alignItems="flex-start" spacing={1}>
    {asterisk ? (
      <Typography
        sx={{
          color: ACCENT_CORAL,
          fontWeight: 700,
          fontSize: '1.5rem',
          lineHeight: 1,
          width: 20,
          textAlign: 'center',
          mt: '-2px',
        }}
      >
        *
      </Typography>
    ) : (
      <CheckCircle fontSize="small" color="primary" sx={{ mt: '2px' }} />
    )}
    <Typography variant="body2">{text}</Typography>
  </Stack>
);

export default PlanComparisonStep;
